'use client';

import React from 'react';
import { useParams, useRouter, useSearchParams } from 'next/navigation';

import { Container } from '@/components/design/container';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import useTags from '@/hooks/queries/useTags';
import useTranslation from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import Filter from '@/svgs/filter.svg';

type Props = object;

const ContentBankFilter: React.FC<Props> = () => {
  const [selectedTags, setSelectedTags] = React.useState<string[]>([]);
  type RouteParams = {
    id: string;
  };
  const { id } = useParams<RouteParams>();

  const { data, isFetched } = useTags({ category_id: id });

  const { t } = useTranslation('pages.contentBank');
  const router = useRouter();
  const searchParams = useSearchParams();
  const params = new URLSearchParams(searchParams.toString());
  const setSelectedTagsFromUrl = (tagsFromUrl: string | null) => {
    if (tagsFromUrl) {
      setSelectedTags(tagsFromUrl.split(','));
    } else {
      setSelectedTags([]);
    }
  };

  React.useEffect(() => {
    const tagsFromUrl = searchParams.get('tags');
    setSelectedTagsFromUrl(tagsFromUrl);
  }, [searchParams]);

  const addFilter = () => {
    if (selectedTags.length > 0) {
      params.set('tags', selectedTags.join(','));
    } else {
      params.delete('tags');
    }
    router.replace(`?${params.toString()}`);
  };

  const cancelFilter = () => {
    const tagsFromUrl = searchParams.get('tags');
    setSelectedTagsFromUrl(tagsFromUrl);
  };

  return (
    <Drawer>
      <DrawerTrigger className="bg-muted px-2 py-[6px] rounded-md flex items-center gap-2 relative">
        {t('filter')} <Filter />
        <div
          className={cn(
            'w-[9px] h-[9px] rounded-[50%] bg-[#B32020] absolute z-2  top-0 right-0',
            searchParams.get('tags') ? 'opacity-[1]' : 'opacity-0'
          )}
        ></div>
      </DrawerTrigger>
      <DrawerContent
        className="min-h-[80vh] max-w-[480px] mr-[auto] ml-[auto]"
        onInteractOutside={(event: { preventDefault: () => void }) => {
          event.preventDefault();
        }}
      >
        <DrawerHeader>
          <DrawerTitle className="flex gap-2 text-[14px]">
            <Filter className="w-[22px]" />
            {t('filter')}
          </DrawerTitle>
          {isFetched && data?.length === 0 ? (
            <div className="text-black">{t('noTagsAvailable')}</div>
          ) : (
            <DrawerDescription className="text-black text-[12px]">
              {t('selectOneOrItems')}
            </DrawerDescription>
          )}
        </DrawerHeader>
        <ScrollArea
          className="w-[100%] h-[calc(80dvh-172px)] rounded-md"
          dir="rtl"
        >
          <Container>
            <ToggleGroup
              dir="rtl"
              type="multiple"
              className="flex-wrap gap-2"
              value={selectedTags}
              onValueChange={(values) => setSelectedTags(values)}
            >
              {data?.map((tag) => (
                <ToggleGroupItem
                  key={tag.id}
                  value={tag.id}
                  aria-label={tag.title}
                  className={cn(
                    ' rounded-[6px] px-4 py-2 text-sm min-w-fit text-black',
                    selectedTags.includes(tag.id) ? 'bg-[#93A5EC]' : 'bg-muted'
                  )}
                >
                  {tag.title}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </Container>
        </ScrollArea>
        <DrawerFooter>
          <DrawerClose className="flex flex-row justify-between gap-4">
            <Button
              disabled={data?.length === 0}
              onClick={() => addFilter()}
              className="w-[45%]"
              variant="default"
            >
              {t('addFilter')}
            </Button>
            <Button
              variant="outline"
              className=" w-[45%]"
              onClick={() => cancelFilter()}
            >
              {t('cancel')}
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default ContentBankFilter;
